import { ProductAttribute, ProductAttributeGroup } from 'types/model/product'
import { contentLanguages } from 'lib/languages'
import { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { t } from 'i18next'
import { formatMultiLanguageField } from 'lib/utils'
import { convertBooleanToString } from './utils'
import { AttributeModal } from './AttributeModal'

type EditNoteModalType = {
  attributeId: string | null
  modal: { right: boolean }
  handleCloseModal: () => void
  attributeGroup: ProductAttributeGroup
  fieldBase: string
}

type FieldValueType =
  | string
  | undefined
  | {
      options: { [lang: string]: string }
      contentLanguage: string
    }
  | boolean

export const EditAttributeModal = ({
  modal,
  handleCloseModal,
  attributeGroup,
  fieldBase,
  attributeId,
}: EditNoteModalType) => {
  const attribute = (attributeGroup.productAttribute.find(
    (att) => att.id === attributeId
  ) || {}) as ProductAttribute
  const { setFieldValue } = useFormikContext()
  const [name, setName] = useState({
    options: formatMultiLanguageField(undefined),
    contentLanguage: contentLanguages[0],
  })
  const [type, setType] = useState<string | null>(null)
  const [facetAttribute, setFacetAttribute] = useState(
    Boolean(attribute.isFacetAttribute)
  )
  const [booleanValue, setBooleanValue] = useState<string | null>('')
  const [numberValue, setNumberValue] = useState<string>('')
  const [stringValue, setStringValue] = useState({
    options: formatMultiLanguageField(undefined),
    contentLanguage: contentLanguages[0],
  })

  useEffect(() => {
    if (convertBooleanToString(attribute.productAttributeValueBoolean)) {
      setType('boolean')
    } else if (attribute.productAttributeValueNumeric) {
      setType('number')
    } else {
      setType('string')
    }
  }, [attributeId])

  useEffect(() => {
    setName(
      attribute.productAttributeName || {
        options: formatMultiLanguageField(undefined),
        contentLanguage: contentLanguages[0],
      }
    )
    setStringValue(
      attribute.productAttributeValueString || {
        options: formatMultiLanguageField(undefined),
        contentLanguage: contentLanguages[0],
      }
    )
    setNumberValue(String(attribute.productAttributeValueNumeric) || '')
    setBooleanValue(
      convertBooleanToString(
        attribute.productAttributeValueBoolean
      )?.toLowerCase() || ''
    )
    setFacetAttribute(attribute.isFacetAttribute)
  }, [attributeId])

  const clearValue = (index: number) => {
    setFieldValue(`${fieldBase}.${index}.productAttributeValueString`, {
      options: formatMultiLanguageField(undefined),
      contentLanguage: contentLanguages[0],
    })
    setFieldValue(`${fieldBase}.${index}.productAttributeValueNumeric`, null)
    setFieldValue(`${fieldBase}.${index}.productAttributeValueBoolean`, null)
  }

  const handleBlur = (fieldName: string, value: FieldValueType) => {
    const attributeIndex = attributeGroup.productAttribute.findIndex(
      (value) => value.id === attribute.id
    )
    if (
      fieldName === 'productAttributeValueNumeric' ||
      fieldName === 'productAttributeValueString' ||
      fieldName === 'productAttributeValueBoolean'
    ) {
      clearValue(attributeIndex)
    }

    setFieldValue(`${fieldBase}.${attributeIndex}.${fieldName}`, value)
  }

  const handleClose = () => {
    handleBlur('isFacetAttribute', facetAttribute)
    handleCloseModal()
  }

  return (
    <AttributeModal
      title={t('editAttribute')}
      modal={modal}
      handleBlur={handleBlur}
      handleClose={handleClose}
      name={name}
      type={type}
      facetAttribute={facetAttribute}
      booleanValue={booleanValue}
      stringValue={stringValue}
      numberValue={numberValue}
      setFacetAttribute={setFacetAttribute}
      setStringValue={setStringValue}
      setNumberValue={setNumberValue}
      setBooleanValue={setBooleanValue}
      setType={setType}
      setName={setName}
    />
  )
}
